/**
 * 강사 성과 리포트 PDF 의 **파일 이름**.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 필요한가
 * ─────────────────────────────────────────────────────────────
 * 브라우저 인쇄는 `document.title` 을 저장 파일명으로 쓴다. 그래서 예전에는
 * 어느 강사의 몇 월 리포트를 뽑아도 전부
 * `Rappo Manager - 헬스 & 피트니스 관리.pdf` 로 떨어졌다.
 * 원장님이 강사 열 명 분을 뽑으면 같은 이름의 파일이 열 개 쌓인다 —
 * 파일을 열기 전에는 누구 것인지 알 수 없고, 다운로드 폴더에서는
 * `(1) (2) (3)` 이 붙어 순서마저 뒤섞인다.
 *
 * ─────────────────────────────────────────────────────────────
 * 형식 —  `Rappo강사성과리포트_권한길_2026-09_머스트무브`
 * ─────────────────────────────────────────────────────────────
 * · 맨 앞이 **고정 접두사**다. 다운로드 폴더에서 이름순 정렬하면 리포트가
 *   한데 모인다. 검색창에 '강사성과' 만 쳐도 걸린다.
 * · 그다음이 **강사 이름**이다. 원장님이 파일을 찾는 기준은 사실상 이것뿐이다.
 * · 달은 `YYYY-MM` 이다. `9월` 이 아니라 이 형식을 쓰는 이유는 **이름순 정렬이
 *   곧 시간순**이 되기 때문이다(`2026-09` < `2026-10`). `2026년 9월` 로 쓰면
 *   10월이 9월보다 앞에 온다.
 * · 센터명이 맨 뒤인 이유는 한 원장님이 보통 한 센터만 뽑기 때문이다 —
 *   앞에 두면 모든 파일이 같은 글자로 시작해 눈으로 구분이 안 된다.
 *   여러 센터를 관리하는 계정에서만 값어치가 있으니 뒤에 둔다.
 *
 * 확장자(`.pdf`)는 **브라우저가 붙인다.** 여기서 붙이면 `....pdf.pdf` 가 된다.
 */

/** 다운로드 폴더에서 리포트를 한데 모으는 고정 접두사 */
export const COACH_REPORT_FILE_PREFIX = 'Rappo강사성과리포트';

/**
 * 파일명에 못 쓰는 문자.
 *
 * 윈도우가 막는 9종(`\ / : * ? " < > |`)을 기준으로 잡는다 — macOS 는 `:` 와 `/`
 * 만 막지만, 원장님이 뽑은 PDF 는 카카오톡·메일로 오간다. **받는 쪽이 윈도우면
 * 저장이 실패한다.** 더 엄격한 쪽에 맞춘다.
 *
 * 강사 이름·센터명은 사람이 자유롭게 입력한 값이라 실제로 들어온다
 * (`머스트무브 강남/역삼`, `PT:1 스튜디오` 처럼).
 */
const FORBIDDEN_CHARS = /[\\/:*?"<>|]/g;

/**
 * 개행·탭·기타 제어문자 — 붙여넣기로 이름 끝에 딸려 들어온다.
 * 파일명에 들어가면 브라우저·OS 마다 다르게 깨진다.
 */
const CONTROL_CHARS = new RegExp('[\\u0000-\\u001F\\u007F]', 'g');

/**
 * 값이 없다는 뜻으로만 쓰이는 문자열.
 *
 * 🔴 화면은 센터명이 없을 때 `—` 를 그린다(CoachPdfReport 의 머리글).
 *    그 화면 값을 그대로 파일명에 넣으면 `..._2026-09_—` 가 된다.
 *    `undefined` · `null` 은 문자열 보간(`${value}`)으로 새어 들어오는 형태다.
 *    셋 다 **빈 값으로 보고 그 자리를 통째로 뺀다** — 값이 없다는 사실을
 *    파일명에 적을 이유가 없다.
 */
const PLACEHOLDER_ONLY = /^(?:[-–—_]+|undefined|null)$/i;

/**
 * 한 칸(segment) 을 파일명에 넣을 수 있는 모양으로 다듬는다.
 *
 * 넣을 수 없으면 **빈 문자열**을 돌려준다. 부르는 쪽이 그 칸을 통째로 빼도록
 * 하기 위해서다 (`_undefined_` 나 `__` 가 파일명에 남지 않게).
 */
const sanitizeSegment = (raw?: string | null): string => {
    if (raw == null) return '';
    const cleaned = String(raw)
        .replace(FORBIDDEN_CHARS, '')
        /*
         * 제어문자는 **공백으로** 바꾼다. 그냥 지우면 줄바꿈으로 나뉘어 있던
         * 두 낱말이 붙어 버린다 (`머스트\n무브` → `머스트무브`).
         * 바로 아래에서 여러 칸이 한 칸으로 줄고, 끝에 남은 것은 trim 이 뗀다.
         */
        .replace(CONTROL_CHARS, ' ')
        /* 이름 안의 공백은 살리되(`머스트 무브`) 여러 칸은 한 칸으로 */
        .replace(/\s+/g, ' ')
        .trim()
        /*
         * 앞뒤의 점은 뗀다. 앞의 점은 유닉스에서 숨김 파일이 되고,
         * 뒤의 점은 브라우저가 붙일 `.pdf` 와 만나 `이름..pdf` 가 된다.
         */
        .replace(/^\.+|\.+$/g, '')
        .trim();

    return PLACEHOLDER_ONLY.test(cleaned) ? '' : cleaned;
};

export type CoachReportFileNameParts = {
    /** 강사 이름 */
    coachName?: string | null;
    /** `YYYY-MM` */
    month?: string | null;
    /** 센터명 — 없으면 그 자리를 뺀다 */
    centerName?: string | null;
};

/**
 * 저장될 PDF 의 이름 (확장자 제외).
 *
 * 빈 칸은 구분자째로 빠지므로 `Rappo강사성과리포트__2026-09` 같은 모양은
 * 나오지 않는다. 전부 비어도 접두사는 남는다 — 이름 없는 파일은 만들지 않는다.
 */
export const buildCoachReportFileName = ({
    coachName,
    month,
    centerName,
}: CoachReportFileNameParts): string =>
    [
        COACH_REPORT_FILE_PREFIX,
        sanitizeSegment(coachName),
        sanitizeSegment(month),
        sanitizeSegment(centerName),
    ]
        .filter((segment) => segment.length > 0)
        .join('_');
